import { useEffect, useRef, useState } from 'react';
import CallButton from './CallButton';

// --- tiny inline markdown ------------------------------------------------
// The agent (gemini-3.5-flash) frequently emits **bold** and bullet/numbered
// lists in Persian. We render these explicitly so customers do not see raw
// asterisks. JSX-only (no dangerouslySetInnerHTML, no markdown library).

const PERSIAN_OR_LATIN_DIGITS = /^\s*[۰-۹0-9]+[.)]\s+/;

function renderInline(text) {
  // Split on **bold** delimiters; render the inner span as <strong>.
  return text.split(/(\*\*[^*\n]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function MessageBody({ content }) {
  // Split into paragraphs by blank-line. Each paragraph is either:
  //  - a bulleted list (every line starts with "* "),
  //  - a numbered list (every line starts with "1." / "۱." etc.),
  //  - or a plain paragraph (whose internal \n preserves line breaks via CSS).
  const paragraphs = content.split(/\n\n+/).filter((p) => p.trim().length > 0);

  return paragraphs.map((para, pi) => {
    const lines = para.split('\n').filter((l) => l.length > 0);
    const isUL = lines.length > 1 && lines.every((l) => /^\s*\*\s+/.test(l));
    const isOL = lines.length > 1 && lines.every((l) => PERSIAN_OR_LATIN_DIGITS.test(l));

    if (isUL) {
      return (
        <ul key={pi} className="consult-msg-list">
          {lines.map((l, li) => (
            <li key={li}>{renderInline(l.replace(/^\s*\*\s+/, ''))}</li>
          ))}
        </ul>
      );
    }
    if (isOL) {
      return (
        <ol key={pi} className="consult-msg-list">
          {lines.map((l, li) => (
            <li key={li}>{renderInline(l.replace(PERSIAN_OR_LATIN_DIGITS, ''))}</li>
          ))}
        </ol>
      );
    }
    return <p key={pi} className="consult-msg-p">{renderInline(para)}</p>;
  });
}

// --- icons (inline SVG, crisp at every size) -----------------------------
// Send icon — left-pointing arrow (the "forward" direction in RTL Persian UI).
const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5M11 5l-6 7 6 7" />
  </svg>
);

const ChatBubbleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" width="24" height="24" aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

export default function ConsultChat({ consult }) {
  const [draft, setDraft] = useState('');
  const logRef = useRef(null);
  const inputRef = useRef(null);

  // While a voice call is active, text input is disabled - the floating widget
  // owns the conversation. CallButton is also disabled by the same condition
  // below so a second call can't start mid-call.
  const inCall = consult.callState && consult.callState !== 'idle';
  const inputDisabled = !consult.ready || consult.busy || inCall;

  // Scroll the log to the bottom whenever messages change OR the typing
  // indicator toggles, so a new turn always reveals the latest content.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [consult.messages, consult.busy]);

  // Keep the chat input focused across turns. It gets `disabled` while the
  // agent's reply streams in (which makes the browser blur it), so the moment
  // it becomes editable again we refocus it: the customer can fire off message
  // after message with Enter and never has to click back into the box. Skipped
  // while a call is active, since the call owns the conversation then.
  useEffect(() => {
    if (!inputDisabled) inputRef.current?.focus();
  }, [inputDisabled]);

  const send = () => {
    const text = draft.trim();
    if (!text || consult.busy) return;
    setDraft('');
    consult.sendMessage(text);
  };

  return (
    <>
      <div className="consult-log" ref={logRef}>
        {!consult.ready && (
          <div className="consult-empty">
            <span className="consult-empty-icon"><ChatBubbleIcon /></span>
            <span className="consult-empty-hint">در حال آماده‌سازی گفتگو…</span>
          </div>
        )}

        {consult.ready && consult.messages.length === 0 && (
          <div className="consult-empty">
            <span className="consult-empty-icon"><ChatBubbleIcon /></span>
            <span className="consult-empty-title">سلام 👋</span>
            <span className="consult-empty-hint">
              برای شروع، یک پیام بنویسید؛ مثلاً «می‌خواهم فیبر نوری برای خانه‌ام بگیرم».
            </span>
          </div>
        )}

        {consult.messages.map((m) => (
          <div key={m.id} className={`consult-msg ${m.role}`}>
            {m.role === 'agent' ? <MessageBody content={m.content} /> : m.content}
          </div>
        ))}

        {consult.busy && (
          <div className="consult-typing" role="status" aria-label="اپراتور در حال نوشتن">
            <span className="consult-typing-dot" />
            <span className="consult-typing-dot" />
            <span className="consult-typing-dot" />
          </div>
        )}
      </div>

      {consult.registrationUrl && (
        <a
          href={consult.registrationUrl}
          className="consult-registration-cta"
          role="button"
          aria-label="رفتن به صفحه ثبت‌نام اشتراک فیبر نوری"
        >
          <span className="consult-registration-cta-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
          </span>
          <span className="consult-registration-cta-text">رفتن به صفحهٔ ثبت‌نام</span>
          <span className="consult-registration-cta-arrow" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                 strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </a>
      )}

      <div className="consult-inputbar">
        <input
          ref={inputRef}
          name="message"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="پیام خود را بنویسید…"
          disabled={inputDisabled}
          aria-label="متن پیام"
          autoComplete="off"
        />
        <CallButton
          onClick={consult.startCall}
          disabled={!consult.ready || consult.callState !== 'idle'}
        />
        <button
          className="consult-send-btn"
          onClick={send}
          disabled={inputDisabled || !draft.trim()}
          aria-label="ارسال پیام"
        >
          <SendIcon />
        </button>
      </div>
    </>
  );
}
